import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Hammer, Search, Settings } from 'lucide-react';
import { api, LitSquadInventoryEntry } from '@/lib/api';
import { useAuth } from '@/auth/hooks/useAuth';
import { useArcheAdmin } from '@/auth/hooks/useArcheAdmin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { ArcheHeader } from '../../../components/ArcheHeader';
import { AvatarEditor } from '../components/AvatarEditor';
import { DeleteItemDialog } from '../components/DeleteItemDialog';
import { InventoryGrid } from '../components/InventoryGrid';
import { TransferHistoryList } from '../components/TransferHistoryList';
import { TransferItemDialog } from '../components/TransferItemDialog';
import { useAvatarEditorState } from '../hooks/useAvatarEditorState';
import { useAvatarUpload } from '../hooks/useAvatarUpload';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import { useLitSquadInventory } from '../hooks/useLitSquadInventory';
import { useLitSquadTransferHistory } from '../hooks/useLitSquadTransferHistory';
import { getLitSquadSpecies } from '../utils';

export function InventoryPage() {
  const { user } = useAuth();
  // Server route gate: `requireAdminOrLitcraftAdmin` (docs/api-admin.md).
  const isAdmin = useArcheAdmin('litcraft_admin');

  const { search, debouncedSearch, handleSearchChange } = useDebouncedSearch();
  const [sortBy, setSortBy] = useState('name');

  const { items, total, loading, error, refresh, loadMore, hasMore } = useLitSquadInventory({
    search: debouncedSearch || undefined,
    sortBy,
  });

  const [transferItem, setTransferItem] = useState<LitSquadInventoryEntry | null>(null);
  const [deleteItem, setDeleteItem] = useState<LitSquadInventoryEntry | null>(null);
  const navigate = useNavigate();

  // Avatar editor state lives at the page level so the inventory cards
  // (siblings of the editor) can drive `setBase` / `addAccessory` from
  // their per-card buttons.
  const avatarEditor = useAvatarEditorState();
  const avatarUpload = useAvatarUpload();

  // Species ("dog", "bear", …) of the currently-selected base character,
  // or null when no base is selected. Drives the species-gating that
  // hides incompatible accessory buttons in the inventory grid.
  const currentAvatarBaseSpecies = useMemo<string | null>(() => {
    if (!avatarEditor.baseItemId) return null;
    const baseItem = items.find((i) => i.itemId === avatarEditor.baseItemId);
    return baseItem ? getLitSquadSpecies(baseItem) : null;
  }, [avatarEditor.baseItemId, items]);

  const handleSetAvatarBase = useCallback(
    (item: LitSquadInventoryEntry) => {
      if (!item.itemId) return;
      // Switching base species invalidates any accessory whose species
      // differs from the new base. Drop them rather than ship an avatar
      // with a Crab snowboard on a Dog.
      const newSpecies = getLitSquadSpecies(item);
      for (const accId of avatarEditor.accessoryItemIds) {
        const acc = items.find((i) => i.itemId === accId);
        if (!acc || getLitSquadSpecies(acc) !== newSpecies) {
          avatarEditor.removeAccessory(accId);
        }
      }
      avatarEditor.setBase(item.itemId);
    },
    [avatarEditor, items],
  );

  const handleAddAvatarAccessory = useCallback(
    (item: LitSquadInventoryEntry) => {
      if (!item.itemId) return;
      // Defensive guard: the inventory grid hides the button on
      // mismatched species, but a programmatic call could still fire
      // (e.g. test or future UI surface). Reject silently.
      const accSpecies = getLitSquadSpecies(item);
      if (
        currentAvatarBaseSpecies === null ||
        accSpecies === null ||
        accSpecies !== currentAvatarBaseSpecies
      ) {
        return;
      }
      avatarEditor.addAccessory(item.itemId);
    },
    [avatarEditor, currentAvatarBaseSpecies],
  );

  const handleTransfer = useCallback(
    async (toUsername: string) => {
      if (!transferItem?.itemId) return;
      await api.transferLitSquadItem({ toUsername, itemId: transferItem.itemId });
      refresh();
    },
    [transferItem, refresh],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteItem?.itemId) return;
    await api.burnLitSquadItem(deleteItem.itemId);
    refresh();
  }, [deleteItem, refresh]);

  const handleItemClick = useCallback(
    (item: LitSquadInventoryEntry) => {
      if (item.itemId) navigate(`/a/arche_litsquad/item/${item.itemId}`);
    },
    [navigate],
  );

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_litsquad" title="My LitSquad">
        <Link to="/a/arche_litsquad/craft">
          <Button size="sm">
            <Hammer className="h-4 w-4 mr-2" />
            Craft
          </Button>
        </Link>
        {isAdmin && (
          <Link to="/a/arche_litsquad/admin">
            <Button variant="outline" size="sm">
              <Settings className="h-4 w-4 mr-2" />
              Admin
            </Button>
          </Link>
        )}
      </ArcheHeader>
      <div className="max-w-6xl mx-auto px-4 w-full">
        <p className="text-muted-foreground text-sm mt-4 mb-2">{total} NFTs</p>

        <div className="mb-6">
          <AvatarEditor editor={avatarEditor} upload={avatarUpload} items={items} />
        </div>

        <Tabs defaultValue="inventory">
          <TabsList>
            <TabsTrigger value="inventory">Inventory</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="inventory" className="mt-4">
            <div className="flex gap-3 mb-6 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search LitSquad NFTs..."
                  className="pl-9"
                />
              </div>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="acquired_at">Recently Acquired</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {error && (
              <div className="text-center py-4 text-destructive mb-4">
                <p>{error}</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={refresh}>
                  Retry
                </Button>
              </div>
            )}

            <InventoryGrid
              items={items}
              loading={loading}
              onTransfer={setTransferItem}
              onDelete={setDeleteItem}
              onClick={handleItemClick}
              onSetAvatarBase={handleSetAvatarBase}
              onAddAvatarAccessory={handleAddAvatarAccessory}
              currentAvatarBaseSpecies={currentAvatarBaseSpecies}
            />

            {hasMore && (
              <div className="text-center mt-6">
                <Button variant="outline" onClick={loadMore} disabled={loading}>
                  {loading ? 'Loading...' : 'Load More'}
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <TransferHistoryContent userId={user?.userId ?? ''} />
          </TabsContent>
        </Tabs>

        <TransferItemDialog
          item={transferItem}
          open={!!transferItem}
          onClose={() => setTransferItem(null)}
          onConfirm={handleTransfer}
        />

        <DeleteItemDialog
          item={deleteItem}
          open={!!deleteItem}
          onClose={() => setDeleteItem(null)}
          onConfirm={handleDelete}
        />
      </div>
    </div>
  );
}

function TransferHistoryContent({ userId }: { userId: string }) {
  const { transfers, loading, error, loadMore, hasMore } = useLitSquadTransferHistory();

  return (
    <>
      {error && (
        <div className="text-center py-4 text-destructive mb-4">
          <p>{error}</p>
        </div>
      )}
      <TransferHistoryList
        transfers={transfers}
        currentUserId={userId}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={loadMore}
      />
    </>
  );
}
